import React, { useState, useEffect } from 'react';
import { ref, push, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database as db, storage, auth } from '../../firebaseConfig';
import './SaveTemplatePopup.css';

const WIDGET_TYPE_CATEGORIES = [
    { value: 'Header', label: 'Header' },
    { value: 'Footer', label: 'Footer' },
    { value: 'Services', label: 'Services' },
    { value: 'Q&A', label: 'Q&A' },
    { value: 'Testimonials', label: 'Testimonials' },
    { value: 'Content', label: 'Content' },
    { value: 'Media', label: 'Media' },
    { value: 'Product', label: 'Product' },
    { value: 'Contact', label: 'Contact' },
    { value: 'Other', label: 'Other' }
];

function SaveTemplatePopup({ isOpen, onClose, widgetData, originalWidgetType, colors = {} }) {
    const [templateName, setTemplateName] = useState('');
    const [widgetType, setWidgetType] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Reset form when popup opens
    useEffect(() => {
        if (isOpen) {
            setTemplateName('');
            setWidgetType('');
            setError('');
        }
    }, [isOpen]);

    // 🚀 Save HeroHeader Template - Preserves ALL original properties and colors
    const handleSaveTemplate = async () => {
        if (!templateName.trim()) {
            setError('Please enter a template name');
            return;
        }
        
        if (!widgetType) {
            setError('Please select a widget type');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            console.log('💾 [SAVE TEMPLATE] Starting save process for HeroHeader...');
            console.log('📦 [SAVE TEMPLATE] Original widgetData:', widgetData);

            // 🎯 HeroHeader Template Optimization 2025
            // ✅ KEEP ALL PROPERTIES - No color removal, no property stripping
            // ✅ PRESERVE: containerSections, contentElements, colors, margins, layouts
            const cleanedWidgetData = { ...widgetData };
            
            // ⚠️ ONLY REMOVE: Internal widget management properties (NOT design properties!)
            const fieldsToRemove = [
                'id',           // Widget instance ID
                'isSelected',   // UI state
                'position',     // Page position
                'key',          // React key
                'ref'           // React ref
            ];
            
            fieldsToRemove.forEach(field => {
                delete cleanedWidgetData[field];
            });
            
            // 🧹 Clean undefined/null values recursively (keeps structure clean)
            // ⚠️ CRITICAL: Keep FALSE values (e.g. backgroundGradientEnabled: false)
            // ⚠️ CRITICAL: Keep 0 values (e.g. paddingTop: 0)
            const removeUndefinedValues = (obj) => {
                if (Array.isArray(obj)) {
                    return obj
                        .filter(item => item !== undefined && item !== null)
                        .map(item => typeof item === 'object' && item !== null ? removeUndefinedValues(item) : item);
                }
                
                if (typeof obj === 'object' && obj !== null) {
                    const cleaned = {};
                    Object.keys(obj).forEach(key => {
                        const value = obj[key];
                        
                        // ✅ Keep: false, 0, empty string (all are valid values!)
                        // ❌ Remove: undefined, null
                        if (value !== undefined && value !== null) {
                            if (typeof value === 'object') {
                                const cleanedNested = removeUndefinedValues(value);
                                // ✅ Keep empty arrays and objects if they exist (intentional empty state)
                                if (Array.isArray(cleanedNested) ? cleanedNested.length > 0 : Object.keys(cleanedNested).length > 0) {
                                    cleaned[key] = cleanedNested;
                                }
                            } else {
                                // ✅ Keep all primitive values: string, number, boolean (including 0, false, '')
                                cleaned[key] = value;
                            }
                        }
                    });
                    return cleaned;
                }
                
                return obj;
            };
            
            const finalWidgetData = removeUndefinedValues(cleanedWidgetData);
            
            console.log('🔍 [SAVE TEMPLATE] Widget-Level Background Check:', {
                backgroundColor: finalWidgetData.backgroundColor,
                backgroundOpacity: finalWidgetData.backgroundOpacity,
                backgroundGradientEnabled: finalWidgetData.backgroundGradientEnabled,
                backgroundGradientColor1: finalWidgetData.backgroundGradientColor1,
                backgroundGradientColor2: finalWidgetData.backgroundGradientColor2,
                backgroundImage: finalWidgetData.backgroundImage
            });
            
            // 🔍 DEBUG: Check Container-Level properties
            if (finalWidgetData.containerSections) {
                console.log('🔍 [SAVE TEMPLATE] Container Sections Check:', finalWidgetData.containerSections.length);
                finalWidgetData.containerSections.forEach((container, i) => {
                    console.log(`  📦 Container ${i}:`, {
                        containerBackgroundColor: container.containerBackgroundColor,
                        containerBackgroundGradientEnabled: container.containerBackgroundGradientEnabled,
                        contentElements: container.contentElements?.length || 0
                    });
                    
                    // Check first text element's maxWidth
                    const firstTextElement = container.contentElements?.find(el => el.type === 'text');
                    if (firstTextElement) {
                        console.log(`  📝 First Text Element:`, {
                            maxWidth: firstTextElement.maxWidth,
                            maxWidthMobile: firstTextElement.maxWidthMobile,
                            fontSize: firstTextElement.fontSize,
                            color: firstTextElement.color
                        });
                    }
                });
            }
            
            // ✅ PRESERVE ORIGINAL COLORS - Don't override, keep as-is!
            // Colors are already in widgetData (backgroundColor, leftBackgroundColor, etc.)
            // We store current brand colors separately for reference
            finalWidgetData.savedWithColors = colors;

            console.log('✅ [SAVE TEMPLATE] Cleaned widgetData:', finalWidgetData);
            console.log('🎨 [SAVE TEMPLATE] Preserved colors:', {
                backgroundColor: finalWidgetData.backgroundColor,
                leftBackgroundColor: finalWidgetData.leftBackgroundColor,
                rightBackgroundColor: finalWidgetData.rightBackgroundColor,
                savedWithColors: finalWidgetData.savedWithColors
            });
            console.log('📦 [SAVE TEMPLATE] Container sections:', finalWidgetData.containerSections?.length || 0);

            // Template data structure - optimized for HeroHeader
            const templateData = {
                templateName: templateName.trim(),
                widgetType: widgetType,
                originalWidgetType: originalWidgetType || 'HeroHeader',
                widgetData: finalWidgetData,
                userId: user.uid,
                createdAt: new Date().toISOString(),
                // Metadata for better template management
                isHeroHeaderTemplate: true,
                hasContainerSections: !!finalWidgetData.containerSections
            };

            // Save to Firebase
            const templatesRef = ref(db, `customWidgetTemplates/${user.uid}`);
            const newTemplateRef = push(templatesRef);
            
            // Add templateId to the data
            templateData.templateId = newTemplateRef.key;
            
            await set(newTemplateRef, templateData);

            console.log('✅ [SAVE TEMPLATE] Template saved successfully:', templateData.templateId);
            console.log('📊 [SAVE TEMPLATE] Template stats:', {
                name: templateData.templateName,
                category: templateData.widgetType,
                containerSections: finalWidgetData.containerSections?.length || 0,
                hasColors: !!finalWidgetData.savedWithColors
            });
            
            // Success - close popup and reset form
            onClose();
            setTemplateName('');
            setWidgetType('');

        } catch (err) {
            console.error('❌ [SAVE TEMPLATE] Error saving template:', err);
            setError('Failed to save template: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="save-template-popup-overlay">
            <div className="save-template-popup">
                <div className="save-template-header">
                    <h2>Save as Template</h2>
                    <button 
                        className="save-template-close-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        ×
                    </button>
                </div>

                <div className="save-template-content">
                    {error && (
                        <div className="save-template-error">
                            {error}
                        </div>
                    )}

                    {/* Template Name */}
                    <div className="save-template-field">
                        <label>Template Name</label>
                        <input
                            type="text"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="e.g. Modern Blue Header"
                            disabled={saving}
                        />
                    </div>

                    {/* Widget Type */}
                    <div className="save-template-field">
                        <label>Category</label>
                        <select
                            value={widgetType}
                            onChange={(e) => setWidgetType(e.target.value)}
                            disabled={saving}
                        >
                            <option value="">Select category...</option>
                            {WIDGET_TYPE_CATEGORIES.map(category => (
                                <option key={category.value} value={category.value}>
                                    {category.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="save-template-footer">
                    <button 
                        className="save-template-cancel-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button 
                        className="save-template-save-btn"
                        onClick={handleSaveTemplate}
                        disabled={saving || !templateName.trim() || !widgetType}
                    >
                        {saving ? 'Saving...' : 'Save Template'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SaveTemplatePopup;
